<script setup lang="ts">
import { computed, ref } from "vue";
import AddToCartButton from "@/components/buttons/AddToCartButton.vue";
import Product from "~/models/Product";
import { StockSelectorCssClass } from "~/config/product";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import FastPickupIcon from "~/components/icons/FastPickupIcon.vue";

const props = defineProps({
  product: {
    type: Product,
    required: true,
  },
});

const selectedQty = ref<number>(0);
const stockTotal = computed((): number => {
  return props.product.stock_total;
});
const stockQtyItems = computed((): number[] => {
  const maxQty = Math.min(stockTotal.value, 10);
  return Array.from({ length: maxQty }, (_, i) => i + 1);
});
const ssrWidth = useResponsiveSsrWidth();
const breakpoints = useBreakpoints(breakpointsBootstrapV5, { ssrWidth });
const isMobileSize = breakpoints.smallerOrEqual("md");
const selectQty = (qty: number) => {
  selectedQty.value = qty;
};

if (stockTotal.value > 0) {
  selectQty(1);
}
</script>

<template>
  <section
    v-if="stockTotal"
    class="row g-0 g-md-2 border-top border-bottom border-light-subtle py-2"
    :class="[StockSelectorCssClass]"
  >
    <div class="col-2 col-md-auto">
      <div class="btn-group qty-selector h-100">
        <button
          class="btn btn-light dropdown-toggle text-secondary"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-auto-close="true"
          aria-expanded="false"
        >
          {{ selectedQty }}
        </button>
        <ul class="dropdown-menu">
          <li v-for="qty in stockQtyItems" :key="qty">
            <a
              class="dropdown-item"
              href="#"
              :class="{ active: qty == selectedQty }"
              @click.prevent="selectQty(qty)"
            >
              {{ qty }}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div class="col-5 col-md-auto d-flex align-items-center">
      <div class="dropdown store-selector w-100 h-100">
        <button
          class="btn text-info mw-100 dropdown-toggle p-1 text-start lh-1 h-100"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <div
            class="text-truncate w-100"
            :class="{
              'small-sm fw-semibold': isMobileSize,
              'd-inline-block': product.stock_stores === 0,
            }"
            :style="{ maxWidth: isMobileSize ? '90%' : 'auto' }"
          >
            Ver disponible en tiendas
          </div>
          <div
            v-if="product.stock_stores > 0"
            class="small-sm mt-1 d-inline-block align-middle"
          >
            <span v-if="isMobileSize" class="text-secondary">En </span>
            <span v-else class="text-secondary">Recoge en </span>
            <span class="text-nowrap">
              <span
                class="text-danger fw-bold"
                :class="{ 'small-xs': isMobileSize }"
                >15 minutos</span
              >
              <fast-pickup-icon class="lh-normal" />
            </span>
          </div>
        </button>
        <ul class="dropdown-menu">
          <li
            v-for="item in product.stores_with_stock"
            :key="item.store_id || 0"
          >
            <a
              class="dropdown-item d-flex align-items-center justify-content-between gap-2"
              href="#"
              @click.prevent
            >
              <span>{{ item.store_name }}</span>
              <i
                class="bi bi-check-lg text-success fs-6 align-middle ms-auto"
              />
              <div
                v-if="item.fast_pickup"
                class="small-xs d-inline-block align-middle"
              >
                <span class="text-nowrap">
                  <span class="text-danger fw-bold">15 minutos</span>
                  <div class="d-inline-block ms-1 clock-lines">
                    <div class="border-top border-danger w-100" />
                    <div class="border-top border-danger w-100" />
                    <div class="border-top border-danger w-100" />
                  </div>
                  <i class="bi bi-clock-fill text-danger align-middle" />
                </span>
              </div>
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div
      class="col-5 col-md-auto ms-auto"
      :class="
        product.is_web_purchasable ? 'text-end' : 'flex-grow-1 text-center'
      "
    >
      <add-to-cart-button
        v-if="product.is_web_purchasable"
        v-slot="slotProps"
        :product="product"
        :btn-add-from-search="true"
        :to="$route.fullPath"
      >
        <button
          class="btn btn-danger rounded-0 px-3 px-md-5 py-3 text-nowrap ms-auto"
          @click="slotProps.click(selectedQty)"
        >
          {{
            isMobileSize
              ? "Agregar al carrito"
              : "&nbsp;&nbsp;Agregar al carrito&nbsp;&nbsp;"
          }}
        </button>
      </add-to-cart-button>
      <div v-else class="d-flex justify-content-center align-items-center">
        <h4 class="m-0 text-danger">Disponible solo en sucursal</h4>
      </div>
    </div>
  </section>
  <section v-else>
    <h3 class="m-0 text-danger">AGOTADO TEMPORALMENTE</h3>
  </section>
</template>

<style lang="scss">
.qty-selector {
  width: 50px;
  height: 40px;
  .dropdown-menu {
    width: 50px;
    min-width: 50px;
    li a {
      height: 40px;
      line-height: 40px;
      padding: 0;
      text-align: center;
    }
  }
  .btn {
    padding-right: 20px;
  }
  .btn::before,
  .btn::after {
    font-family: "bootstrap-icons";
    border: none;
    position: absolute;
    right: 3px;
    font-weight: 900;
    font-size: 0.8rem;
    color: var(--bs-gray-400);
  }
  .btn::before {
    content: "\F286";
    top: 6px;
  }
  .btn::after {
    content: "\F282";
    bottom: 6px;
  }
}
.small-xs .clock-lines {
  width: 6px;
  margin-right: 1px;
  div {
    margin: 1px 0;
    border-width: 0.095rem !important;
    margin-left: auto;
  }
}
.store-selector {
  .dropdown-item:active {
    * {
      color: white !important;
      border-color: white !important;
    }
  }
}
</style>
